import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc, increment, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { getCachedStreamUrl, prefetchStreamUrl } from '../../utils/bunnyStream';
import VideoPlayer from '../../components/dashboard/VideoPlayer';
import LockedOverlay from '../../components/dashboard/LockedOverlay';
import QuizEngine from '../../components/dashboard/QuizEngine';
import DOMPurify from 'dompurify';
import {
  ArrowRight, ArrowLeft, Play, Loader2, AlertCircle, ChevronDown, ChevronUp,
  Lock, CheckCircle2, Clock, FileText, Award, FileEdit, Medal, BookOpen,
} from 'lucide-react';
import AssignmentSubmission from '../../components/dashboard/AssignmentSubmission';
import { sendNotification } from '../../utils/notificationService';

const formatDur = (min, sec) => {
  const m = parseInt(min) || 0;
  const s = parseInt(sec) || 0;
  if (m === 0 && s === 0) return '';
  return `${m}:${String(s).padStart(2, '0')}`;
};

const getLessonTheme = (type, isActive, isLocked) => {
  if (isLocked) {
    return {
      bg: isActive ? 'bg-slate-200 dark:bg-zinc-700' : 'bg-slate-100 dark:bg-zinc-800/50',
      iconColor: 'text-slate-400 dark:text-zinc-500',
      hover: 'hover:bg-slate-50 dark:hover:bg-zinc-800',
      activeBorder: 'border-r-slate-400',
      activeBg: 'bg-slate-50 dark:bg-zinc-800/80',
      Icon: Lock, label: 'مغلق', badge: 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'
    };
  }

  switch (type) {
    case 'video': return {
      bg: isActive ? 'bg-blue-100 dark:bg-blue-500/20' : 'bg-blue-50 dark:bg-blue-500/10',
      iconColor: isActive ? 'text-blue-600 dark:text-blue-400' : 'text-blue-500 dark:text-blue-400',
      hover: 'hover:bg-blue-50/50 dark:hover:bg-blue-500/5',
      activeBorder: 'border-r-blue-500', activeBg: 'bg-blue-50/50 dark:bg-blue-900/10',
      Icon: Play, label: 'فيديو', badge: 'text-blue-700 bg-blue-100 dark:bg-blue-500/20 dark:text-blue-400'
    };
    case 'text': return {
      bg: isActive ? 'bg-amber-100 dark:bg-amber-500/20' : 'bg-amber-50 dark:bg-amber-500/10',
      iconColor: isActive ? 'text-amber-600 dark:text-amber-400' : 'text-amber-500 dark:text-amber-400',
      hover: 'hover:bg-amber-50/50 dark:hover:bg-amber-500/5',
      activeBorder: 'border-r-amber-500', activeBg: 'bg-amber-50/50 dark:bg-amber-900/10',
      Icon: FileText, label: 'مقال', badge: 'text-amber-700 bg-amber-100 dark:bg-amber-500/20 dark:text-amber-400'
    };
    case 'quiz': return {
      bg: isActive ? 'bg-purple-100 dark:bg-purple-500/20' : 'bg-purple-50 dark:bg-purple-500/10',
      iconColor: isActive ? 'text-purple-600 dark:text-purple-400' : 'text-purple-500 dark:text-purple-400',
      hover: 'hover:bg-purple-50/50 dark:hover:bg-purple-500/5',
      activeBorder: 'border-r-purple-500', activeBg: 'bg-purple-50/50 dark:bg-purple-900/10',
      Icon: Award, label: 'اختبار', badge: 'text-purple-700 bg-purple-100 dark:bg-purple-500/20 dark:text-purple-400'
    };
    case 'assignment': return {
      bg: isActive ? 'bg-emerald-100 dark:bg-emerald-500/20' : 'bg-emerald-50 dark:bg-emerald-500/10',
      iconColor: isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-emerald-500 dark:text-emerald-400',
      hover: 'hover:bg-emerald-50/50 dark:hover:bg-emerald-500/5',
      activeBorder: 'border-r-emerald-500', activeBg: 'bg-emerald-50/50 dark:bg-emerald-900/10',
      Icon: FileEdit, label: 'تكليف', badge: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-500/20 dark:text-emerald-400'
    };
    case 'final_project': return {
      bg: isActive ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-rose-50 dark:bg-rose-500/10',
      iconColor: isActive ? 'text-rose-600 dark:text-rose-400' : 'text-rose-500 dark:text-rose-400',
      hover: 'hover:bg-rose-50/50 dark:hover:bg-rose-500/5',
      activeBorder: 'border-r-rose-500', activeBg: 'bg-rose-50/50 dark:bg-rose-900/10',
      Icon: Medal, label: 'مشروع نهائي', badge: 'text-rose-700 bg-rose-100 dark:bg-rose-500/20 dark:text-rose-400'
    };
    default: return {
      bg: isActive ? 'bg-slate-200 dark:bg-zinc-700' : 'bg-slate-50 dark:bg-white/5',
      iconColor: isActive ? 'text-slate-700 dark:text-zinc-300' : 'text-slate-500 dark:text-zinc-500',
      hover: 'hover:bg-slate-100 dark:hover:bg-white/10',
      activeBorder: 'border-r-slate-400', activeBg: 'bg-slate-50 dark:bg-zinc-800',
      Icon: Play, label: 'فيديو', badge: 'text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-400'
    };
  }
};

export default function CourseWatch() {
  const { slugOrId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const [courseId, setCourseId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [isDescOpen, setIsDescOpen] = useState(true);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [openUnits, setOpenUnits] = useState({});
  const [activeLesson, setActiveLesson] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [lessonProgress, setLessonProgress] = useState({});
  const [showToast, setShowToast] = useState(false);

  const toggleUnit = (idx) => setOpenUnits(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Compute unlock status based on sequential hierarchy
  const lessonUnlockStatus = useMemo(() => {
    const status = {};
    if (!course?.curriculum) return status;

    let isPrevCompleted = true;

    course.curriculum.forEach((unit, ui) => {
      unit.lessons?.forEach((lesson, li) => {
        const id = `u${ui}-l${li}`;
        status[id] = isPrevCompleted;
        isPrevCompleted = !!lessonProgress[id];
      });
    });
    return status;
  }, [course, lessonProgress]);

  useEffect(() => {
    if (!slugOrId) return;
    const fetchCourse = async () => {
      try {
        const coursesRef = collection(db, 'courses');
        const q = query(coursesRef, where('slug', '==', slugOrId));
        const querySnapshot = await getDocs(q);

        let data = null;
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0];
          data = { id: docData.id, ...docData.data() };
        } else {
          // Fallback
          const docRef = doc(db, 'courses', slugOrId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            data = { id: docSnap.id, ...docSnap.data() };
          }
        }

        if (data) {
          setCourse(data);
          setCourseId(data.id);
          if (data.curriculum?.length) setOpenUnits({ 0: true });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [slugOrId]);

  useEffect(() => {
    if (!currentUser || !courseId) return;
    const check = async () => {
      const enrQ = query(collection(db, 'enrollments'), where('userId', '==', currentUser.uid), where('courseId', '==', courseId));
      const enrSnap = await getDocs(enrQ);
      if (!enrSnap.empty) {
        setIsEnrolled(true);
        const eid = enrSnap.docs[0].id;
        setEnrollmentId(eid);
        const data = enrSnap.docs[0].data();
        if (data.completedLessons) {
          const pMap = {};
          data.completedLessons.forEach(l => pMap[l] = true);
          setLessonProgress(pMap);
        }
        return;
      }
      const regQ = query(collection(db, 'registrations'), where('userId', '==', currentUser.uid), where('courseId', '==', courseId));
      const regSnap = await getDocs(regQ);
      if (!regSnap.empty && regSnap.docs.some(d => d.data().status === 'customer')) setIsEnrolled(true);
    };
    check();
  }, [currentUser, courseId]);

  useEffect(() => {
    if (!course?.curriculum) return;
    const lessonParam = searchParams.get('lesson');
    if (lessonParam) {
      const [uStr, lStr] = lessonParam.split('-').map(s => parseInt(s.replace(/\D/g, '')));
      const unit = course.curriculum[uStr];
      if (unit?.lessons?.[lStr]) {
        setActiveLesson({ unitIdx: uStr, lessonIdx: lStr, ...unit.lessons[lStr], unitTitle: unit.title });
        setQuizPassed(false);
        return;
      }
    }
    const firstUnit = course.curriculum[0];
    if (firstUnit?.lessons?.[0]) {
      setActiveLesson({ unitIdx: 0, lessonIdx: 0, ...firstUnit.lessons[0], unitTitle: firstUnit.title });
    }
  }, [course, searchParams]);

  const canWatch = useCallback((lesson, ui, li) => {
    if (!lesson) return false;
    const hasSubAccess = lesson.accessLevel === 'public' || isEnrolled;
    if (!hasSubAccess) return false;

    if (isEnrolled && ui !== undefined && li !== undefined) {
      return lessonUnlockStatus[`u${ui}-l${li}`];
    }
    return true;
  }, [isEnrolled, lessonUnlockStatus]);

  useEffect(() => {
    const lessonType = activeLesson?.type || 'video';
    if (lessonType !== 'video') { setStreamUrl(null); return; }
    const videoId = activeLesson?.videoFilename || activeLesson?.videoUrl;
    const isPublic = activeLesson?.accessLevel === 'public';
    if (!videoId || (!currentUser && !isPublic) || (!isPublic && !canWatch(activeLesson, activeLesson?.unitIdx, activeLesson?.lessonIdx))) {
      setStreamUrl(null); return;
    }

    // Generate signed URL client-side — instant, no backend needed
    let cancelled = false;
    setIsFetchingUrl(true);
    getCachedStreamUrl(videoId).then(url => {
      if (!cancelled) {
        setStreamUrl(url);
        setIsFetchingUrl(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setStreamUrl(null);
        setIsFetchingUrl(false);
      }
    });

    return () => { cancelled = true; };
  }, [activeLesson, currentUser, isEnrolled, canWatch]);

  const selectLesson = (ui, li, lesson, unitTitle) => {
    setActiveLesson({ unitIdx: ui, lessonIdx: li, ...lesson, unitTitle });
    setSearchParams({ lesson: `u${ui}-l${li}` });
    setQuizPassed(false);
  };

  const markAsComplete = async () => {
    if (!currentUser || !courseId || !activeLesson || !enrollmentId) return true;
    const lessonIdStr = `u${activeLesson.unitIdx}-l${activeLesson.lessonIdx}`;
    if (lessonProgress[lessonIdStr]) return true;

    try {
      const progRef = doc(db, 'enrollments', enrollmentId, 'progress', lessonIdStr);
      await setDoc(progRef, { completed: true, points: 100, type: activeLesson.type, completedAt: new Date() });
      await updateDoc(doc(db, 'enrollments', enrollmentId), {
        pointsEarned: increment(100),
        completedLessons: arrayUnion(lessonIdStr)
      });
      setLessonProgress(prev => ({ ...prev, [lessonIdStr]: true }));

      const totalLessons = course?.curriculum?.reduce((acc, unit) => acc + (unit.lessons?.length || 0), 0) || course?.totalLessons || 0;
      if (Object.keys(lessonProgress).length + 1 === totalLessons && course?.instructorId) {
        await sendNotification(course.instructorId, 'إنجاز دورة', `الطالب ${currentUser.displayName || ''} أتم للتو دورة "${course.title}" بنسبة 100%!`, 'success', '/instructor-dashboard/students');
      }

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleCompleteAndNext = async () => {
    if (!activeLesson) return;
    const lessonIdStr = `u${activeLesson.unitIdx}-l${activeLesson.lessonIdx}`;

    // If it's a quiz that isn't passed yet, we shouldn't allow bypass if it's locked
    if (activeLesson.type === 'quiz' && !quizPassed && !lessonProgress[lessonIdStr] && isEnrolled) {
      return; // Quiz must be passed via QuizEngine
    }

    if (!lessonProgress[lessonIdStr] && isEnrolled) {
      const success = await markAsComplete();
      if (!success) return;
    }

    const next = findAdjacentLesson('next');
    if (next) {
      selectLesson(next.ui, next.li, next.lesson, next.unitTitle);
    }
  };

  const handleQuizComplete = useCallback(async (score) => {
    if (score === 100) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    }
    if (!activeLesson) return;
    const lessonIdStr = `u${activeLesson.unitIdx}-l${activeLesson.lessonIdx}`;
    if (!lessonProgress[lessonIdStr] && score >= (activeLesson.quizData?.passingScore || 60)) {
      if (!enrollmentId) return;
      try {
        const progRef = doc(db, 'enrollments', enrollmentId, 'progress', lessonIdStr);
        await setDoc(progRef, { completed: true, points: 100, type: 'quiz', completedAt: new Date() });
        await updateDoc(doc(db, 'enrollments', enrollmentId), {
          pointsEarned: increment(100),
          completedLessons: arrayUnion(lessonIdStr)
        });
        setLessonProgress(prev => ({ ...prev, [lessonIdStr]: true }));

        const totalLessons = course?.curriculum?.reduce((acc, unit) => acc + (unit.lessons?.length || 0), 0) || course?.totalLessons || 0;
        if (Object.keys(lessonProgress).length + 1 === totalLessons && course?.instructorId) {
          await sendNotification(course.instructorId, 'إنجاز دورة', `الطالب ${currentUser?.displayName || ''} أتم للتو دورة "${course.title}" بنسبة 100%!`, 'success', '/instructor-dashboard/students');
        }
      } catch (err) { console.error(err); }
    }
  }, [activeLesson, enrollmentId, lessonProgress, course, currentUser]);

  const findAdjacentLesson = (direction) => {
    if (!course || !activeLesson) return null;
    const cur = course.curriculum || [];
    let ui = activeLesson.unitIdx;
    let li = activeLesson.lessonIdx;
    if (direction === 'next') {
      li++;
      if (li >= cur[ui]?.lessons?.length) { ui++; li = 0; }
    } else {
      li--;
      if (li < 0) { ui--; li = (cur[ui]?.lessons?.length || 1) - 1; }
    }
    if (ui < 0 || ui >= cur.length) return null;
    const lesson = cur[ui]?.lessons?.[li];
    if (!lesson) return null;
    return { ui, li, lesson, unitTitle: cur[ui].title };
  };

  const nextLesson = findAdjacentLesson('next');
  const prevLesson = findAdjacentLesson('prev');

  const activeLessonIdStr = `u${activeLesson?.unitIdx}-l${activeLesson?.lessonIdx}`;
  const isCurrentLessonCompleted = !!lessonProgress[activeLessonIdStr];
  const isNextLocked = isEnrolled && !isCurrentLessonCompleted;

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <Loader2 className="w-12 h-12 animate-spin text-primary-500" />
      <p className="font-bold mt-4 text-slate-500">جاري التحميل...</p>
    </div>
  );

  if (notFound || !course) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500">
      <AlertCircle className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
      <h2 className="text-2xl font-bold mb-2">الدورة غير موجودة</h2>
      <Link to="/courses" className="text-primary-600 font-bold mt-4">العودة للدورات</Link>
    </div>
  );

  const curriculum = course.curriculum || [];

  const totalLessonsCount = curriculum.reduce((acc, unit) => acc + (unit.lessons?.length || 0), 0) || course.totalLessons || 0;
  const completedCount = Object.keys(lessonProgress).length;
  const progressPct = totalLessonsCount > 0 ? Math.min(100, Math.round((completedCount / totalLessonsCount) * 100)) : 0;
  const firstName = currentUser?.displayName ? currentUser.displayName.split(' ')[0] : 'بطل';
  
  let encouragePhrase = "";
  if (progressPct === 100) {
    encouragePhrase = `أبدعت يا ${firstName}! أتممت الدورة بنجاح.`;
  } else if (progressPct >= 75) {
    encouragePhrase = `الأمتار الأخيرة يا ${firstName}، واصل تقدمك!`;
  } else if (progressPct >= 50) {
    encouragePhrase = `تجاوزت النصف يا ${firstName}، عمل ممتاز!`;
  } else if (progressPct >= 25) {
    encouragePhrase = `تطور رائع يا ${firstName}، استمر.`;
  } else if (progressPct > 0) {
    encouragePhrase = `بداية موفقة يا ${firstName}!`;
  } else {
    encouragePhrase = `هيا نبدأ الإبداع يا ${firstName}!`;
  }

  const renderContentArea = () => {
    if (!activeLesson) return (
      <div className="aspect-video bg-slate-100 dark:bg-black/40 backdrop-blur-md rounded-lg flex flex-col items-center justify-center border border-slate-200/50 dark:border-white/10 shadow-xl">
        <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mb-6">
          <Play className="w-10 h-10 text-slate-300 dark:text-zinc-600" />
        </div>
        <p className="text-slate-500 dark:text-zinc-400 font-bold text-xl">اختر درساً للبدء</p>
      </div>
    );

    const activeIsSubscriptionLocked = activeLesson.accessLevel === 'subscriber' && !isEnrolled;
    const activeIsSequentiallyLocked = isEnrolled && !lessonUnlockStatus[`u${activeLesson.unitIdx}-l${activeLesson.lessonIdx}`];

    if (activeIsSequentiallyLocked) {
      return (
        <div className="aspect-video bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl rounded-lg flex flex-col items-center justify-center border border-slate-200/50 dark:border-white/10 shadow-2xl p-8 md:p-16 text-center overflow-hidden relative">
          <div className="absolute inset-0 bg-amber-500/5 blur-[120px] pointer-events-none"></div>
          <div className="w-24 h-24 rounded-3xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mb-8 border border-amber-100 dark:border-amber-500/20 shadow-inner z-10">
            <Lock className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-800 dark:text-white mb-4 z-10 tracking-tight">الدرس مغلق حالياً</h2>
          <p className="text-slate-500 dark:text-zinc-400 font-bold text-lg md:text-xl max-w-lg leading-relaxed z-10">يجب عليك إكمال الدرس الذي يسبقه والضغط على <strong className="text-primary-600 dark:text-primary-400">تحديد كمكتمل</strong> لتتمكن من مشاهدة هذه المحطة في مسارك التعليمي.</p>
        </div>
      );
    }

    if (activeIsSubscriptionLocked) return (
      <LockedOverlay thumbnailUrl={activeLesson.thumbnailUrl || course.thumbnail} courseId={slugOrId} />
    );

    const lessonType = activeLesson.type || 'video';

    if (lessonType === 'text') {
      const safeHtml = DOMPurify.sanitize(activeLesson.textContent || '<p>لا يوجد محتوى لهذا المقال.</p>');
      return (
        <div className="bg-white dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-lg p-8 md:p-16 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none"></div>
          <div
            className="prose prose-slate dark:prose-invert max-w-none
              prose-headings:font-black prose-p:leading-8 prose-p:text-slate-600
              dark:prose-p:text-zinc-400 prose-li:text-slate-600 dark:prose-li:text-zinc-400
              prose-strong:text-slate-900 dark:prose-strong:text-white
              prose-a:text-primary-600 text-lg leading-9 font-medium"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </div>
      );
    }

    if (lessonType === 'quiz') {
      return (
        <div className="flex flex-col relative w-full bg-white dark:bg-zinc-900/60 backdrop-blur-3xl rounded-lg shadow-sm overflow-hidden p-6 md:p-10">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
          <QuizEngine
            quizData={activeLesson.quizData}
            lessonId={`u${activeLesson.unitIdx}-l${activeLesson.lessonIdx}`}
            courseId={activeLesson.id}
            currentUser={currentUser}
            onPassStatusChange={(passed, score) => {
              setQuizPassed(passed);
              if (passed && score !== undefined) handleQuizComplete(score);
            }}
          />
        </div>
      );
    }

    if (lessonType === 'assignment' || lessonType === 'final_project') {
      return (
        <AssignmentSubmission
          sessionData={activeLesson}
          courseId={courseId}
          courseTitle={course.title}
          currentUser={currentUser}
          onSuccess={() => markAsComplete()}
        />
      );
    }

    // Default: video
    if (isFetchingUrl) return (
      <div className="aspect-video bg-slate-900 dark:bg-black rounded-lg flex flex-col items-center justify-center shadow-2xl border border-white/5">
        <Loader2 className="w-12 h-12 animate-spin text-primary-500 mb-6" />
        <p className="text-white/70 text-lg font-bold tracking-wider">جاري تجهيز البث الآمن...</p>
      </div>
    );

    if (streamUrl) return (
      <div className="rounded-lg overflow-hidden shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)] border border-white/10 bg-black">
        <VideoPlayer streamUrl={streamUrl} poster={activeLesson.thumbnailUrl || course.thumbnail} />
      </div>
    );

    return (
      <div className="aspect-video bg-slate-100 dark:bg-black/40 backdrop-blur-md rounded-lg flex flex-col items-center justify-center border border-slate-200/50 dark:border-white/10 shadow-xl">
        <AlertCircle className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
        <p className="text-slate-500 dark:text-zinc-400 font-bold text-xl">لا يوجد فيديو صالح لهذا الدرس.</p>
      </div>
    );
  };

  return (
    <div className="animate-in fade-in duration-700 w-full min-h-screen relative pb-12" dir="rtl">
      {/* Cinematic Background */}
      <div className="fixed inset-0 pointer-events-none bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.02] dark:opacity-5 mix-blend-overlay -z-10"></div>
      <div className="fixed inset-0 pointer-events-none bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#050505] dark:to-[#0a0a0a] -z-20"></div>

      <div className="mb-8 px-6 lg:px-10 pt-6 flex items-center justify-between sticky top-0 bg-white/70 dark:bg-black/50 backdrop-blur-xl z-50 border-b border-slate-200/50 dark:border-white/5 shadow-sm">
        <Link to={`/courses/${slugOrId}`}
          className="inline-flex items-center gap-3 text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white font-bold transition-colors group text-[1.05rem] py-4">
          <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          العودة للمركز الفني
        </Link>
        <h1 className="text-xl font-black text-slate-900 dark:text-white truncate max-w-lg hidden sm:block drop-shadow-sm">{course.title}</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 px-6 lg:px-10 mt-8 max-w-[100rem] mx-auto relative">
        <div className="flex-1 min-w-0">
          {/* Sticky Cinematic Content Area */}
          <div className=" z-30 w-full -mt-2 pt-2 pb-6 bg-slate-50 dark:bg-[#050505] transition-all  duration-300">
            <div className="max-h-[calc(100vh-160px)] overflow-y-auto custom-scrollbar rounded-md shadow-sm  overflow-hidden relative group">
              {renderContentArea()}
            </div>
            <div className="flex items-center gap-3 m-4 sm:gap-4 pt-4">
              {prevLesson && (
                <button
                  onClick={() => selectLesson(prevLesson.ui, prevLesson.li, prevLesson.lesson, prevLesson.unitTitle)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3.5 rounded-xl bg-white dark:bg-zinc-800/80 hover:bg-slate-100 dark:hover:bg-zinc-700/80 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white font-bold text-sm sm:text-lg transition-all shadow-sm"
                >
                  <ArrowRight className="w-5 h-5" />
                  <span className="hidden sm:inline">الدرس السابق</span>
                  <span className="sm:hidden">السابق</span>
                </button>
              )}
              {nextLesson && (
                <button
                  onClick={handleCompleteAndNext}
                  disabled={activeLesson.type === 'quiz' && isNextLocked && !quizPassed}
                  className={`flex-[2] flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 rounded-xl font-bold text-sm sm:text-lg transition-all overflow-hidden relative group ${activeLesson.type === 'quiz' && isNextLocked && !quizPassed
                    ? 'bg-slate-100 dark:bg-zinc-900/80 border border-slate-200 dark:border-white/10 text-slate-400 dark:text-zinc-600 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-500 text-white shadow-xl shadow-primary-500/20 active:translate-y-0.5 hover:-translate-y-0.5 border border-primary-500/50'
                    }`}
                >
                  {!(activeLesson.type === 'quiz' && isNextLocked && !quizPassed) && (
                    <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out"></div>
                  )}
                  <div className="relative z-10 flex items-center gap-2">
                    {activeLesson.type === 'quiz' && isNextLocked && !quizPassed ? <Lock className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                    <span>{isCurrentLessonCompleted ? 'الدرس التالي' : 'التالي'}</span>
                    <ArrowLeft className="w-5 h-5" />
                  </div>
                </button>
              )}
            </div>
            {isNextLocked && activeLesson.type === 'quiz' && !quizPassed && (
              <p className="text-sm sm:text-base text-amber-600 dark:text-amber-500 font-bold flex items-center gap-2 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 border border-amber-100 dark:border-amber-500/20 rounded-xl shadow-sm mt-3">
                <Lock className="w-4 h-4 sm:w-5 sm:h-5" /> يجب اجتياز الاختبار للمتابعة.
              </p>
            )}
          </div>

          {activeLesson && (
            <div className="mt-6 space-y-4">
              <div>
                <div className="relative mb-8 pb-4 border-b-2 border-slate-100 dark:border-white/5 group">
                  {/* Breadcrumb-style Unit Indicator */}
                  <div className="flex items-center gap-2 mb-4 animate-in slide-in-from-right duration-700">
                    <span className="w-6 h-1 rounded-full bg-primary-500"></span>
                    <span className="text-lg sm:text-[12px] font-black text-primary-600 dark:text-primary-400 uppercase tracking-widest">
                      {activeLesson.unitTitle || 'محتوى الدورة'}
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                      {(() => {
                        const t = activeLesson.type || 'video';
                        const theme = getLessonTheme(t, true, false);
                        const Icon = theme.Icon;
                        return (
                          <span className={`inline-flex items-center gap-2 text-md sm:text-sm font-black px-4 py-1.5 rounded-lg ${theme.badge}  transition-all duration-300`}>
                            <Icon className="w-4 h-4" /> {theme.label}
                          </span>
                        );
                      })()}
                      <h1 className="text-2xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight leading-tight transition-all">
                        {activeLesson.title || 'بدون عنوان'}
                      </h1>
                    </div>

                    {/* Meta Info Pills */}
                    <div className="flex flex-wrap items-center gap-3 text-slate-500 dark:text-zinc-400 font-bold text-sm">
                      {activeLesson.durationMin > 0 && (
                        <div className="flex items-center gap-2 bg-slate-50 dark:bg-white/5 px-4 py-1.5 rounded-lg border border-slate-200/50 dark:border-white/10 ">
                          <Clock className="w-4 h-4 text-primary-500" />
                          <span className="tabular-nums text-lg">{formatDur(activeLesson.durationMin, activeLesson.durationSec)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 bg-slate-50/50 dark:bg-white/5 px-4 py-1.5 rounded-lg border border-transparent dark:border-white/5 opacity-80">
                        <BookOpen className="w-4 h-4" />
                        <span className='text-lg'>{activeLesson.unitTitle}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Modern Collapsible Description */}
                {activeLesson.description && (
                  <div className="bg-white/50 dark:bg-white/5 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden mt-6 shadow-sm transition-all duration-500 hover:shadow-md">
                    <button
                      onClick={() => setIsDescOpen(!isDescOpen)}
                      className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-slate-50/50 dark:hover:bg-white/5 transition-colors text-right"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center text-primary-600 dark:text-primary-400 shadow-sm">
                          <FileText className="w-5 h-5" />
                        </div>
                        <span className="text-lg font-black text-slate-900 dark:text-zinc-100 tracking-tight">تفاصيل الدرس</span>
                      </div>
                      <div className={`p-2 rounded-full bg-slate-100 dark:bg-zinc-800 transition-transform duration-500 ${isDescOpen ? 'rotate-180' : ''}`}>
                        <ChevronDown className="w-5 h-5 text-slate-500 dark:text-zinc-400" />
                      </div>
                    </button>

                    <div className={`transition-all duration-700 ease-in-out overflow-hidden ${isDescOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="px-5 sm:px-6 pb-8 pt-2">
                        <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 dark:via-white/10 to-transparent mb-6"></div>
                        <div className="prose prose-slate dark:prose-invert max-w-none">
                          <p className="text-[1.05rem] text-slate-600 dark:text-zinc-300 leading-relaxed font-medium whitespace-pre-wrap">
                            {activeLesson.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}


              </div>


            </div>
          )}
        </div>

        <div className="w-full lg:w-80 xl:w-[26rem] shrink-0">
          <div className="bg-white/70 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-lg overflow-hidden shadow-md sticky top-[104px]">
            <div className="px-6 py-5 border-b border-slate-200/50 dark:border-white/5 bg-slate-50/50 dark:bg-black/30">
              <h3 className="font-black text-slate-900 dark:text-white text-xl">خريطة التعلم</h3>
              <p className="text-md font-medium text-slate-500 dark:text-zinc-400 mt-1">
                {course.totalUnits || curriculum.length} وحدات • {totalLessonsCount} دروس
              </p>

              {/* Progress Bar Section */}
              <div className="mt-6 mb-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                    {encouragePhrase}
                  </span>
                  <span className="text-sm font-black text-primary-600 dark:text-primary-400">
                    {progressPct}%
                  </span>
                </div>
                <div className="h-2.5 w-full bg-slate-200/70 dark:bg-white/10 rounded-full overflow-hidden shadow-inner">
                  <div 
                    className="h-full bg-gradient-to-l from-primary-600 to-primary-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="max-h-[calc(100vh-14rem)] overflow-y-auto no-scrollbar pb-6">
              {curriculum.map((unit, ui) => {
                const isOpen = openUnits[ui];
                return (
                  <div key={ui}>
                    <button onClick={() => toggleUnit(ui)}
                      className="w-full px-6 py-5 flex items-center justify-between text-right hover:bg-slate-100/50 dark:hover:bg-white/5 transition-colors border-b border-slate-100/50 dark:border-white/5 group">
                      <div>
                        <h4 className="text-[1.05rem] font-black text-slate-900 dark:text-white group-hover:text-primary-600 transition-colors">الفصل {ui + 1}: {unit.title || 'بدون عنوان'}</h4>
                        <p className="text-mdfont-bold text-slate-500 dark:text-zinc-400 mt-1.5">{unit.lessons?.length || 0} دروس إبداعية</p>
                      </div>
                      <div className="w-10 h-10 rounded-full border border-slate-200 dark:border-white/10 flex items-center justify-center bg-slate-50 dark:bg-black/20 group-hover:border-primary-500/30 transition-all">
                        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                      </div>
                    </button>
                    {isOpen && unit.lessons?.map((lesson, li) => {
                      const isActive = activeLesson?.unitIdx === ui && activeLesson?.lessonIdx === li;
                      const isSubscriptionLocked = lesson.accessLevel === 'subscriber' && !isEnrolled;
                      const isSequentiallyLocked = isEnrolled && !lessonUnlockStatus[`u${ui}-l${li}`];
                      const isLocked = isSubscriptionLocked || isSequentiallyLocked;
                      const lt = lesson.type || 'video';
                      const theme = getLessonTheme(lt, isActive, isLocked);
                      const { Icon } = theme;

                      return (
                        <button key={li}
                          onClick={() => { if (!isLocked) selectLesson(ui, li, lesson, unit.title); }}
                          onMouseEnter={() => {
                            if (!isLocked && (lesson.type || 'video') === 'video') {
                              const vid = lesson.videoFilename || lesson.videoUrl;
                              if (vid) prefetchStreamUrl(vid);
                            }
                          }}
                          title={isSequentiallyLocked ? 'يجب إكمال الدروس السابقة لفتح هذا الدرس' : (isSubscriptionLocked ? 'يتطلب هذا الدرس الاشتراك بالدورة' : '')}
                          className={`w-full px-5 py-4 flex items-center justify-between gap-3 text-right transition-all border-b border-slate-100/50 dark:border-white/5 group ${isActive
                            ? `${theme.activeBg} border-r-4 ${theme.activeBorder}`
                            : `${theme.hover}`}`}>
                          <div className="flex items-center gap-4 min-w-0">
                            {lessonProgress[`u${ui}-l${li}`] && (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            )}
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border border-slate-100 dark:border-white/5 transition-colors shadow-sm ${theme.bg}`}>
                              <Icon className={`w-5 h-5 ${theme.iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0 pr-1 text-right">
                              <div className="flex items-center gap-2">
                                <p className={`text-[1.05rem] truncate ${isActive ? `font-black ${theme.iconColor}` : (isLocked ? 'font-bold text-slate-400 dark:text-zinc-500' : 'font-bold text-slate-700 dark:text-zinc-300')}`}>
                                  <span className="opacity-60 text-md ml-1.5 font-black shrink-0">{li + 1}.</span>
                                  {lesson.title || 'بدون عنوان'}
                                </p>
                              </div>
                            </div>
                          </div>
                          {lt === 'video' && formatDur(lesson.durationMin, lesson.durationSec) && !isLocked && (
                            <span className="flex items-center gap-1.5 text-md font-bold text-slate-500 dark:text-zinc-500 bg-white dark:bg-black/40 px-2 py-1 rounded-md border border-slate-200 dark:border-white/5 shrink-0 shadow-sm ml-1">
                              <Clock className="w-3.5 h-3.5 text-primary-500 dark:text-primary-400" />
                              <span dir="ltr" className="font-mono tracking-widest">{formatDur(lesson.durationMin, lesson.durationSec)}</span>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Toast */}
      {showToast && (
        <div className="fixed bottom-10 right-10 z-[100] animate-in slide-in-from-bottom-10 fade-in duration-500" dir="rtl">
          <div className="bg-zinc-950 border border-primary-500/50 rounded-2xl p-6 shadow-[0_0_40px_rgba(218,165,32,0.4)] flex items-center gap-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-l from-primary-600/20 to-transparent"></div>
            <div className="relative w-14 h-14 bg-primary-500/10 rounded-full flex items-center justify-center border border-primary-500/30 shrink-0">
              <span className="text-3xl animate-bounce">🏆</span>
            </div>
            <div className="relative">
              <h4 className="text-primary-400 font-black text-xl mb-1 tracking-wide">وسام التميز</h4>
              <p className="text-zinc-300 font-bold text-md mt-0.5">أحسنت! علامة كاملة 100/100</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
